import { useApolloClient, useMutation } from '@vue/apollo-composable';
import { useCacheCleanup } from '@precon-tools-suite-by-shared/shared-graphql-client-js-apollo';
import {
    AttachTagsInput,
    DetachTagsInput,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import {
    NodesTagsNextPageDocument,
    TagNodeDocument,
    UntagNodeDocument,
} from './useNodesTags.generated';
import { computed, ref } from 'vue';

export function useNodesTags() {
    const { client } = useApolloClient();

    const _loading = ref<boolean>(false);
    const loading = computed(() => _loading.value);

    const fetchMoreTagsForNode = (
        id: string,
        after: string | null | undefined = null,
        first = 5
    ) => {
        _loading.value = true;
        return client
            .query({
                query: NodesTagsNextPageDocument,
                variables: { id, first, after },
                fetchPolicy: 'network-only',
            })
            .then((result) => {
                _loading.value = false;
                return result;
            })
            .catch((error) => {
                _loading.value = false;
                throw error;
            });
    };

    return {
        fetchMoreTagsForNode,
        loading,
    };
}

export function useTagNode() {
    const { fetchMoreTagsForNode, loading } = useNodesTags();
    const { clean, restore } = useCacheCleanup();

    const {
        mutate: tag,
        loading: tagging,
        onError: onTaggingError,
        onDone: onTaggingDone,
    } = useMutation(TagNodeDocument);

    const {
        mutate: untag,
        loading: untagging,
        onError: onUntaggingError,
        onDone: onUntaggingDone,
    } = useMutation(UntagNodeDocument);

    const mutating = computed(() => tagging.value || untagging.value);

    const tagNode = async (nodeId: string, tagIds: string | string[]) => {
        const tagsToAttach = typeof tagIds === 'string' ? [tagIds] : tagIds;

        const input = {
            tags: tagsToAttach,
            taggables: [nodeId],
        } as AttachTagsInput;

        return tag({ input })
            .then((result) => {
                if (result?.errors?.length ?? 0 > 0) {
                    console.log('errors while tagging nodes');
                }
                fetchMoreTagsForNode(nodeId);
                return result;
            })
            .catch((error) => {
                throw error;
            });
    };

    const untagNode = async (nodeId: string, tagIds: string | string[]) => {
        const tagsToDetach = typeof tagIds === 'string' ? [tagIds] : tagIds;

        const input = {
            tags: tagsToDetach,
            taggables: [nodeId],
        } as DetachTagsInput;

        const reset = () => restore<'Product'>(nodeId, 'tags', tagsToDetach); // we grab any taggable type

        clean<'Product'>(nodeId, 'tags', tagsToDetach); // we grab any taggable type

        return untag({ input })
            .then((result) => {
                if (result?.errors?.length ?? 0 > 0) {
                    reset();
                }
                fetchMoreTagsForNode(nodeId);
                return result;
            })
            .catch((error) => {
                reset();
                throw error;
            });
    };

    return {
        fetchMoreTagsForNode,
        loading,
        tagNode,
        untagNode,
        mutating,
        onTaggingError,
        onTaggingDone,
        onUntaggingError,
        onUntaggingDone,
    };
}
